import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Target, Users, TrendingUp, Eye, CheckCircle } from 'lucide-react';
import { SalesQualificationStepProps } from '@/types/onboarding/salesQualification';
import { createArrayUpdateHelper } from '../utils';

export const VisionAndGoals: React.FC<SalesQualificationStepProps> = ({ data, onUpdate }) => {
  const updateFormData = (field: string, value: unknown) => {
    onUpdate({ ...data, [field]: value });
  };

  const handleArrayUpdate = createArrayUpdateHelper(data, updateFormData);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Vision & Goals</h2>
        <p className="text-gray-600">
          Define your project requirements and success criteria for enterprise transformation
        </p>
      </div>

      {/* Primary Use Case */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Target className="w-5 h-5 text-red-600" />
            Primary Use Case & Objectives
          </CardTitle>
          <CardDescription>
            What is the main problem you're trying to solve or primary objective?
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="primaryUseCase">Primary Use Case *</Label>
            <Textarea
              id="primaryUseCase"
              value={data.primaryUseCase || ''}
              onChange={(e) => updateFormData('primaryUseCase', e.target.value)}
              placeholder="e.g., Create unified digital experience for residents, streamline board governance, optimize financial management, improve operational efficiency..."
              rows={4}
              required
            />
          </div>

          <div>
            <Label htmlFor="problemStatement">Specific Problem Statement</Label>
            <Textarea
              id="problemStatement"
              value={data.problemStatement || ''}
              onChange={(e) => updateFormData('problemStatement', e.target.value)}
              placeholder="e.g., Current systems are disconnected, residents can't access information easily, board meetings are inefficient, financial reporting is manual..."
              rows={3}
            />
          </div>

          <div>
            <Label htmlFor="desiredOutcome">Desired Outcome</Label>
            <Textarea
              id="desiredOutcome"
              value={data.desiredOutcome || ''}
              onChange={(e) => updateFormData('desiredOutcome', e.target.value)}
              placeholder="e.g., Reduce administrative burden by 50%, increase resident engagement, improve decision-making speed, achieve cost savings..."
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        {/* User Base */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              User Base & Adoption
            </CardTitle>
            <CardDescription>Who will be using the enterprise platform?</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="expectedUsers">Expected Number of Active Users *</Label>
              <Select
                value={data.expectedUsers || ''}
                onValueChange={(value) => updateFormData('expectedUsers', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select user count range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="under-50">Under 50 users</SelectItem>
                  <SelectItem value="50-200">50 - 200 users</SelectItem>
                  <SelectItem value="201-500">201 - 500 users</SelectItem>
                  <SelectItem value="501-1000">501 - 1,000 users</SelectItem>
                  <SelectItem value="1001-5000">1,001 - 5,000 users</SelectItem>
                  <SelectItem value="5001-10000">5,001 - 10,000 users</SelectItem>
                  <SelectItem value="over-10000">Over 10,000 users</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm font-medium">
                Primary User Groups (Check all that apply)
              </Label>
              <div className="grid grid-cols-1 gap-2 mt-2">
                {[
                  'Board Members and Leadership',
                  'Property Management Staff',
                  'Administrative Personnel',
                  'Financial/Accounting Team',
                  'Community Residents',
                  'Vendors and Service Providers',
                  'Legal and Compliance Team',
                  'Maintenance and Operations',
                  'Security Personnel',
                  'Municipal/Government Liaisons',
                ].map((userGroup) => (
                  <div key={userGroup} className="flex items-center space-x-2">
                    <Checkbox
                      id={`user-group-${userGroup}`}
                      checked={data.primaryUserGroups?.includes(userGroup) || false}
                      onCheckedChange={(checked) =>
                        handleArrayUpdate('primaryUserGroups', userGroup, !!checked)
                      }
                    />
                    <Label htmlFor={`user-group-${userGroup}`} className="text-sm">
                      {userGroup}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Critical Features */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Critical Features & Requirements
            </CardTitle>
            <CardDescription>What features are absolutely essential?</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {[
                'Board governance and meeting management',
                'Financial management and treasury tools',
                'Resident communication portal',
                'Vendor management and procurement',
                'Document storage and management',
                'Maintenance request tracking',
                'Event planning and management',
                'Voting and elections system',
                'Compliance and regulatory tools',
                'Mobile-responsive design',
                'Integration with existing systems',
                'Custom reporting and analytics',
                'Multi-language support',
                'Advanced security features',
                'Workflow automation',
                'API access and customization',
              ].map((feature) => (
                <div key={feature} className="flex items-center space-x-2">
                  <Checkbox
                    id={`feature-${feature}`}
                    checked={data.criticalFeatures?.includes(feature) || false}
                    onCheckedChange={(checked) =>
                      handleArrayUpdate('criticalFeatures', feature, !!checked)
                    }
                  />
                  <Label htmlFor={`feature-${feature}`} className="text-sm">
                    {feature}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Success Metrics */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-purple-600" />
            Success Metrics & KPIs
          </CardTitle>
          <CardDescription>
            How will you measure the success of your enterprise implementation?
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              'User adoption rate (% of target users actively using)',
              'Reduction in administrative processing time',
              'Increase in resident engagement and participation',
              'Cost savings from operational efficiencies',
              'Improved decision-making speed',
              'Enhanced communication effectiveness',
              'Better compliance and governance metrics',
              'Increased transparency and accountability',
              'ROI achievement within target timeframe',
              'Successful integration with existing systems',
              'Reduced manual processes and errors',
              'Improved vendor and service provider efficiency',
            ].map((metric) => (
              <div key={metric} className="flex items-start space-x-2">
                <Checkbox
                  id={`success-metric-${metric}`}
                  checked={data.successMetrics?.includes(metric) || false}
                  onCheckedChange={(checked) =>
                    handleArrayUpdate('successMetrics', metric, !!checked)
                  }
                />
                <Label htmlFor={`success-metric-${metric}`} className="text-sm leading-relaxed">
                  {metric}
                </Label>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <Label htmlFor="customMetrics">Custom Success Metrics or KPIs</Label>
            <Textarea
              id="customMetrics"
              value={data.customMetrics || ''}
              onChange={(e) => updateFormData('customMetrics', e.target.value)}
              placeholder="e.g., Specific metrics unique to your community, target percentages, timeline goals..."
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      {/* Long-term Vision */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Eye className="w-5 h-5 text-indigo-600" />
            Long-term Vision & Strategic Goals
          </CardTitle>
          <CardDescription>
            What is your 3-5 year vision for your community and technology?
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="longTermVision">Long-term Vision (3-5 years) *</Label>
            <Textarea
              id="longTermVision"
              value={data.longTermVision || ''}
              onChange={(e) => updateFormData('longTermVision', e.target.value)}
              placeholder="e.g., Become a fully digital, self-sustaining smart community with best-in-class resident services, transparent governance, and efficient operations..."
              rows={4}
              required
            />
          </div>

          <div>
            <Label htmlFor="strategicGoals">Strategic Goals & Initiatives</Label>
            <Textarea
              id="strategicGoals"
              value={data.strategicGoals || ''}
              onChange={(e) => updateFormData('strategicGoals', e.target.value)}
              placeholder="e.g., Expand community amenities, implement sustainability programs, attract younger residents, increase property values..."
              rows={3}
            />
          </div>

          <div>
            <Label htmlFor="technologyRoadmap">Technology Roadmap & Future Needs</Label>
            <Textarea
              id="technologyRoadmap"
              value={data.technologyRoadmap || ''}
              onChange={(e) => updateFormData('technologyRoadmap', e.target.value)}
              placeholder="e.g., IoT integration, smart home connectivity, AI-powered analytics, mobile-first experience..."
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Growth & Scalability Considerations */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Growth & Expansion Plans</CardTitle>
            <CardDescription>How do you expect to grow or change?</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Expected Growth Pattern</Label>
              <Select
                value={data.growthPattern || ''}
                onValueChange={(value) => updateFormData('growthPattern', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select growth expectation" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="stable">Stable - no significant growth expected</SelectItem>
                  <SelectItem value="moderate">Moderate growth (10-30% over 3 years)</SelectItem>
                  <SelectItem value="significant">
                    Significant growth (30-100% over 3 years)
                  </SelectItem>
                  <SelectItem value="rapid">Rapid expansion (100%+ over 3 years)</SelectItem>
                  <SelectItem value="acquisition">
                    Growth through acquisitions or mergers
                  </SelectItem>
                  <SelectItem value="franchise">Franchise or multi-location expansion</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Growth Areas (Check all that apply)</Label>
              {[
                'Additional community properties',
                'Expanded service offerings',
                'New user groups or demographics',
                'Geographic expansion',
                'Partnership opportunities',
                'New revenue streams',
                'Enhanced amenities and facilities',
              ].map((area) => (
                <div key={area} className="flex items-center space-x-2">
                  <Checkbox
                    id={`growth-area-${area}`}
                    checked={data.growthAreas?.includes(area) || false}
                    onCheckedChange={(checked) => handleArrayUpdate('growthAreas', area, !!checked)}
                  />
                  <Label htmlFor={`growth-area-${area}`} className="text-sm">
                    {area}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Innovation & Future-Proofing</CardTitle>
            <CardDescription>What emerging trends interest you?</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {[
                'Artificial Intelligence and automation',
                'Internet of Things (IoT) integration',
                'Smart home and building technology',
                'Sustainability and green initiatives',
                'Electric vehicle infrastructure',
                'Advanced analytics and predictive insights',
                'Blockchain and decentralized systems',
                'Virtual and augmented reality applications',
                'Voice interfaces and smart assistants',
                'Advanced mobile and app capabilities',
                'Community marketplace and sharing economy',
                'Integration with municipal smart city initiatives',
              ].map((trend) => (
                <div key={trend} className="flex items-center space-x-2">
                  <Checkbox
                    id={`trend-${trend}`}
                    checked={data.futureTrends?.includes(trend) || false}
                    onCheckedChange={(checked) =>
                      handleArrayUpdate('futureTrends', trend, !!checked)
                    }
                  />
                  <Label htmlFor={`trend-${trend}`} className="text-sm">
                    {trend}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Help Text */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <p className="text-sm text-amber-700">
          <strong>Vision and goals alignment ensures enterprise solution success:</strong>
        </p>
        <ul className="text-sm text-amber-700 mt-2 space-y-1">
          <li>• Design platform architecture that supports your long-term strategic vision</li>
          <li>• Prioritize features and capabilities that drive your key success metrics</li>
          <li>• Plan scalability and growth capabilities to support future expansion</li>
          <li>• Ensure technology roadmap aligns with community development plans</li>
          <li>• Create implementation phases that deliver early wins towards strategic goals</li>
        </ul>
      </div>
    </div>
  );
};
